import React, { useEffect } from 'react';
import Header from '../components/Header';
import { motion } from 'framer-motion';
import Footer from '../components/Footer';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { gerarLinkWhatsApp } from '../../utils/WhatsappLink';

// Padrão visual compartilhado com as seções da home
const eyebrowSx = (dark) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 1.75,
    py: 0.75,
    borderRadius: "999px",
    backgroundColor: dark ? "rgba(255, 200, 69, 0.12)" : "rgba(255, 200, 69, 0.25)",
    border: dark ? "1px solid rgba(255, 200, 69, 0.4)" : "none",
    color: dark ? "#ffc845" : "#0b243d",
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
});

const primaryButtonSx = {
    px: 4,
    py: 1.75,
    fontSize: "1rem",
    fontWeight: 700,
    textTransform: "none",
    backgroundColor: "#ffc845",
    color: "#0b243d",
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(255, 200, 69, 0.35)",
    transition: "all 0.2s ease",
    '&:hover': {
        backgroundColor: "#e9b53d",
        transform: "translateY(-2px)",
        boxShadow: "0 10px 28px rgba(255, 200, 69, 0.45)",
    },
};

const outlinedButtonSx = (dark) => ({
    px: 4,
    py: 1.75,
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    color: dark ? "#fff" : "#0b243d",
    borderColor: dark ? "rgba(255, 255, 255, 0.4)" : "rgba(11, 36, 61, 0.3)",
    borderRadius: "10px",
    transition: "all 0.2s ease",
    '&:hover': {
        borderColor: dark ? "#fff" : "#0b243d",
        backgroundColor: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(11, 36, 61, 0.04)",
        transform: "translateY(-2px)",
    },
});

const mensagemConsultor = "Olá! Tenho interesse em falar com um consultor sobre os serviços de contabilidade para profissionais da saúde. Poderia me ajudar?";

const Solutions = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const falarComConsultor = () => window.open(gerarLinkWhatsApp(mensagemConsultor), '_blank');

    const profissoes = [
        "Médicos", "Dentistas", "Psicólogos", "Fisioterapeutas", "Fonoaudiólogos",
        "Nutricionistas", "Biomédicos", "Enfermeiros", "Terapeutas ocupacionais",
    ];

    const listSolutions = [
        {
            title: "Troque de Contador por um Serviço que Entende sua Rotina na Saúde",
            description: "Contabilidade para médicos, dentistas, psicólogos, fisioterapeutas e profissionais da área da saúde. Liberte-se da burocracia fiscal e foque no que realmente importa: cuidar de pessoas. Nós cuidamos dos impostos, obrigações e planejamento tributário para que sua atuação na saúde cresça com segurança, organização e tranquilidade.",
            img: "/assets/contador2.jpg"
        },
        {
            title: "Tenha Apoio Especializado em Cada Etapa da Sua Jornada Profissional",
            description: "Desde a abertura do CNPJ até o fechamento de balanços, nossa equipe especializada entende as nuances do setor da saúde. Acompanhamos sua evolução profissional com serviços de contabilidade eficientes, acessíveis e personalizados.",
            img: "/assets/apoioEspecializado.jpg"
        },
        {
            title: "Foco no Atendimento, a Burocracia é Por Nossa Conta",
            description: "Você cuida da saúde dos seus pacientes, e a Clarea cuida da saúde da sua empresa. Nossos planos oferecem suporte completo: impostos, declarações, folha de pagamento e muito mais, com linguagem simples e atendimento humanizado.",
            img: "/assets/focoAtendimento.jpg"
        },
    ];

    return (
        <>
            <Header active="solucoes" />

            {/* Bloco de introdução */}
            <Box
                component="section"
                sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #0b243d 0%, #12365a 100%)",
                    color: "#fff",
                    pt: { xs: "120px", md: "140px" },
                    pb: { xs: 8, md: 10 },
                    overflow: "hidden",
                }}
            >
                {/* Detalhe decorativo */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: { xs: -100, md: -160 },
                        right: { xs: -100, md: -160 },
                        width: { xs: 240, md: 380 },
                        height: { xs: 240, md: 380 },
                        borderRadius: "50%",
                        border: { xs: "36px solid #ffc845", md: "56px solid #ffc845" },
                        opacity: 0.1,
                        pointerEvents: "none",
                    }}
                />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        width: "90%",
                        maxWidth: "1280px",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        alignItems: "center",
                        gap: { xs: 6, md: 8 },
                    }}
                >
                    {/* Imagem (no mobile fica abaixo do texto) */}
                    <Box sx={{ order: { xs: 2, md: 1 } }}>
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <Box sx={{ position: "relative", maxWidth: { xs: "520px", md: "none" }, mx: "auto" }}>
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: { xs: -24, md: -36 },
                                    left: { xs: -24, md: -36 },
                                    width: { xs: 110, md: 160 },
                                    height: { xs: 110, md: 160 },
                                    borderRadius: "50%",
                                    border: { xs: "20px solid #ffc845", md: "28px solid #ffc845" },
                                    opacity: 0.35,
                                }}
                            />
                            <Box
                                component="img"
                                src="/assets/solucoes.jpg"
                                alt="Profissional da saúde"
                                sx={{
                                    position: "relative",
                                    display: "block",
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: "24px",
                                    objectFit: "cover",
                                    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.4)",
                                }}
                            />
                        </Box>
                    </motion.div>
                    </Box>

                    {/* Texto */}
                    <Box sx={{ order: { xs: 1, md: 2 } }}>
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <Box sx={{ ...eyebrowSx(true), mb: 2.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#ffc845" }} />
                            Soluções
                        </Box>
                        <h1 style={{ margin: "0 0 20px", fontSize: "clamp(2rem, 4.4vw, 3.2rem)", fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.5px" }}>
                            Soluções contábeis para{" "}
                            <span style={{ color: "#ffc845" }}>cada profissão da saúde</span>
                        </h1>
                        <Box component="p" sx={{ m: 0, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.7, color: "rgba(255, 255, 255, 0.82)" }}>
                            Na Clarea, entendemos que cada profissão da área da saúde tem suas particularidades — e por isso oferecemos soluções contábeis personalizadas para atender:
                        </Box>

                        {/* Profissões atendidas */}
                        <Box sx={{ mt: 2.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {profissoes.map((profissao) => (
                                <Box
                                    key={profissao}
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.6,
                                        borderRadius: "999px",
                                        backgroundColor: "rgba(255, 255, 255, 0.06)",
                                        border: "1px solid rgba(255, 255, 255, 0.14)",
                                        fontSize: "0.85rem",
                                        fontWeight: 600,
                                        color: "#fff",
                                    }}
                                >
                                    <CheckIcon sx={{ fontSize: 15, color: "#ffc845" }} />
                                    {profissao}
                                </Box>
                            ))}
                            <Box sx={{ display: "inline-flex", alignItems: "center", px: 0.5, fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.7)" }}>
                                e muitos outros profissionais do setor.
                            </Box>
                        </Box>

                        <Box sx={{ mt: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate("../Planos")} sx={primaryButtonSx}>
                                Ver planos
                            </Button>
                            <Button variant="outlined" startIcon={<WhatsAppIcon />} onClick={falarComConsultor} sx={outlinedButtonSx(true)}>
                                Falar com um consultor
                            </Button>
                        </Box>
                    </motion.div>
                    </Box>
                </Box>
            </Box>

            {/* Blocos de soluções */}
            {listSolutions.map((solution, index) => {
                const reverse = index % 2 !== 0;

                return (
                    <Box
                        key={index}
                        component="section"
                        sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            backgroundColor: reverse ? "#f9f2e4" : "#fff",
                            py: { xs: 7, md: 10 },
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                width: "90%",
                                maxWidth: "1280px",
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                alignItems: "center",
                                gap: { xs: 5, md: 8 },
                            }}
                        >
                            {/* Texto */}
                            <Box sx={{ order: { xs: 2, md: reverse ? 2 : 1 } }}>
                                <motion.div
                                    initial={{ opacity: 0, x: reverse ? 50 : -50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.7 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                >
                                    <Box sx={{ ...eyebrowSx(false), mb: 2 }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#ffc845" }} />
                                        Solução {String(index + 1).padStart(2, "0")}
                                    </Box>
                                    <h2 style={{ margin: "0 0 16px", fontSize: "clamp(1.7rem, 3vw, 2.3rem)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px", color: "#0b243d" }}>
                                        {solution.title}
                                    </h2>
                                    <Box component="p" sx={{ m: 0, fontSize: { xs: "1rem", md: "1.05rem" }, lineHeight: 1.75, color: "#4b5563" }}>
                                        {solution.description}
                                    </Box>

                                    <Box sx={{ mt: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                                        <Button variant="contained" startIcon={<WhatsAppIcon />} onClick={falarComConsultor} sx={primaryButtonSx}>
                                            Falar com um consultor
                                        </Button>
                                        <Button variant="outlined" onClick={() => navigate("../Planos")} sx={outlinedButtonSx(false)}>
                                            Ver planos
                                        </Button>
                                    </Box>
                                </motion.div>
                            </Box>

                            {/* Imagem */}
                            <Box sx={{ order: { xs: 1, md: reverse ? 1 : 2 } }}>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.7, delay: 0.15 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                >
                                    <Box sx={{ position: "relative", maxWidth: { xs: "520px", md: "none" }, mx: "auto" }}>
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                [reverse ? "left" : "right"]: { xs: -12, md: -20 },
                                                bottom: { xs: -12, md: -20 },
                                                width: "60%",
                                                height: "60%",
                                                borderRadius: "24px",
                                                backgroundColor: "#0b243d",
                                            }}
                                        />
                                        <Box
                                            component="img"
                                            src={solution.img}
                                            alt={solution.title}
                                            sx={{
                                                position: "relative",
                                                display: "block",
                                                width: "100%",
                                                height: "auto",
                                                borderRadius: "24px",
                                                objectFit: "cover",
                                                boxShadow: "0 20px 50px rgba(11, 36, 61, 0.25)",
                                            }}
                                        />
                                    </Box>
                                </motion.div>
                            </Box>
                        </Box>
                    </Box>
                );
            })}

            {/* Chamada final */}
            <Box
                component="section"
                sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #0b243d 0%, #12365a 100%)",
                    color: "#fff",
                    py: { xs: 8, md: 10 },
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: { xs: -90, md: -120 },
                        left: { xs: -90, md: -120 },
                        width: { xs: 220, md: 320 },
                        height: { xs: 220, md: 320 },
                        borderRadius: "50%",
                        border: { xs: "36px solid #ffc845", md: "52px solid #ffc845" },
                        opacity: 0.12,
                        pointerEvents: "none",
                    }}
                />
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6 }}
                    style={{ position: "relative", zIndex: 1, width: "90%", maxWidth: "760px", textAlign: "center" }}
                >
                    <h2 style={{ margin: "0 0 16px", fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px" }}>
                        Pronto para ter uma contabilidade que <span style={{ color: "#ffc845" }}>entende a saúde?</span>
                    </h2>
                    <Box component="p" sx={{ m: "0 auto", maxWidth: "580px", fontSize: { xs: "1rem", md: "1.08rem" }, lineHeight: 1.7, color: "rgba(255, 255, 255, 0.8)" }}>
                        Escolha o plano ideal para a sua rotina ou fale com um consultor para tirar suas dúvidas.
                    </Box>
                    <Box sx={{ mt: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "center", gap: 1.5 }}>
                        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate("../Planos")} sx={primaryButtonSx}>
                            Ver planos
                        </Button>
                        <Button variant="outlined" startIcon={<WhatsAppIcon />} onClick={falarComConsultor} sx={outlinedButtonSx(true)}>
                            Falar com um consultor
                        </Button>
                    </Box>
                    <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, fontSize: "0.9rem", color: "rgba(255, 255, 255, 0.75)" }}>
                        <CheckIcon sx={{ fontSize: 18, color: "#ffc845" }} />
                        Sem multa e sem fidelidade
                    </Box>
                </motion.div>
            </Box>

            <Footer />
        </>
    );
};

export default Solutions;
